import { LucideIcon, Copy, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface CopyableInfoRowProps {
  icon: LucideIcon;
  label: string;
  value: string;
  /** Quando ausente, o botão de copiar não é exibido (ex.: e-mail vazio). */
  onCopy?: () => void;
  isCopied?: boolean;
  copyTooltip?: string;
}

/** Card de informação com botão de copiar e feedback animado por campo. */
export function CopyableInfoRow({
  icon: Icon,
  label,
  value,
  onCopy,
  isCopied = false,
  copyTooltip = "Copiar",
}: CopyableInfoRowProps) {
  return (
    <div className="group flex items-center gap-3 rounded-xl border border-border/50 bg-card p-3 shadow-sm transition-all duration-300 hover:border-primary/30">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition-all group-hover:bg-primary/10 group-hover:text-primary dark:bg-slate-800/50">
        <Icon className="h-[18px] w-[18px]" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p className="truncate text-sm font-medium text-foreground">{value}</p>
      </div>

      {onCopy && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:bg-slate-100 hover:text-foreground dark:hover:bg-slate-800"
                  onClick={onCopy}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {isCopied ? (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        key="check"
                      >
                        <Check className="h-4 w-4 text-emerald-600" />
                      </motion.span>
                    ) : (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        key="copy"
                      >
                        <Copy className="h-4 w-4" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              }
            />
            <TooltipContent>{isCopied ? "Copiado!" : copyTooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
